import { convertToRaw, ContentState } from 'draft-js';
import { stateToMarkdown } from 'draft-js-export-markdown';
import { stateFromMarkdown } from 'draft-js-import-markdown';
import htmlToDraft from 'html-to-draftjs';
import draftToHtml from 'draftjs-to-html';

/**
 * HTML Processing Utility Class
 * Centralizes all HTML processing logic to ensure consistency
 */
class HtmlProcessor {
  /**
   * Safely decode HTML entities
   */
  static decodeHtmlEntities(html: string): string {
    if (!html) return '';

    try {
      const textArea = document.createElement('textarea');
      textArea.innerHTML = html;
      return textArea.value;
    } catch (error) {
      console.error('Error decoding HTML entities:', error);
      // Fallback manual decode
      return html
        .replace(/&lt;/g, '<')
        .replace(/&gt;/g, '>')
        .replace(/&amp;/g, '&')
        .replace(/&quot;/g, '"')
        .replace(/&#39;/g, "'");
    }
  }

  /**
   * Clean and normalize HTML content
   */
  static cleanHtml(html: string): string {
    if (!html) return '';

    return (
      html
        // Normalize whitespace
        .replace(/\s+/g, ' ')
        .replace(/\n\s*\n/g, '\n')
        // Fix self-closing tags
        .replace(/<br\s*\/?>/gi, '<br>')
        .replace(/<hr\s*\/?>/gi, '<hr>')
        // Remove dangerous attributes
        .replace(/<(\w+)[^>]*?(class="[^"]*"|style="[^"]*")[^>]*>/gi, '<$1 $2>')
        // Clean up empty elements
        .replace(/<p>\s*<\/p>/g, '<p><br></p>')
        .trim()
    );
  }

  /**
   * Fix list formatting issues
   */
  static fixListFormatting(html: string): string {
    if (!html) return '';

    return (
      html
        // Remove completely empty list items
        .replace(/<li><\/li>/g, '')
        .replace(/<li>\s*<\/li>/g, '')
        .replace(/<li><p><\/p><\/li>/g, '')
        .replace(/<li><p>\s*<\/p><\/li>/g, '')
        .replace(/<li><br><\/li>/g, '')
        // Fix nested list structure
        .replace(/<\/li>\s*<([uo]l)>/g, '<$1>')
        .replace(/<\/([uo]l)>\s*<\/li>/g, '</$1></li>')
        // Fix consecutive lists
        .replace(/<\/ul>\s*<ul>/g, '')
        .replace(/<\/ol>\s*<ol>/g, '')
        // Ensure list items have content
        .replace(/<li>(\s*)<\/li>/g, '<li>$1<br></li>')
    );
  }
}

/**
 * Improved HTML to markdown conversion with better encoding handling
 * @param html HTML content from editor
 * @returns markdown string
 */
export const htmlToMarkdown = (html: string): string => {
  try {
    if (!html) return '';

    // Step 1: Decode any HTML entities that might be causing display issues
    let processedHtml = HtmlProcessor.decodeHtmlEntities(html);

    // Step 2: Clean and normalize the HTML
    processedHtml = HtmlProcessor.cleanHtml(processedHtml);

    // Step 3: Fix list formatting
    processedHtml = HtmlProcessor.fixListFormatting(processedHtml);

    // Step 4: Convert to ContentBlocks using html-to-draftjs
    const blocksFromHTML = htmlToDraft(processedHtml);
    if (!blocksFromHTML?.contentBlocks) {
      console.warn('Failed to parse HTML with html-to-draftjs, using fallback');
      return fallbackHtmlToMarkdown(processedHtml);
    }

    const contentState = ContentState.createFromBlockArray(
      blocksFromHTML.contentBlocks,
      blocksFromHTML.entityMap
    );

    // Step 5: Convert to markdown
    const markdown = stateToMarkdown(contentState);
    return markdown || fallbackHtmlToMarkdown(processedHtml);
  } catch (error) {
    console.error('Error converting HTML to markdown:', error);
    return fallbackHtmlToMarkdown(html);
  }
};

/**
 * Fallback HTML to markdown conversion for when Draft.js fails
 */
function fallbackHtmlToMarkdown(html: string): string {
  if (!html) return '';

  try {
    // Decode HTML entities first
    let markdown = HtmlProcessor.decodeHtmlEntities(html);

    // Convert headers
    markdown = markdown
      .replace(/<h1[^>]*>(.*?)<\/h1>/gi, '\n# $1\n\n')
      .replace(/<h2[^>]*>(.*?)<\/h2>/gi, '\n## $1\n\n')
      .replace(/<h3[^>]*>(.*?)<\/h3>/gi, '\n### $1\n\n')
      .replace(/<h4[^>]*>(.*?)<\/h4>/gi, '\n#### $1\n\n')
      .replace(/<h5[^>]*>(.*?)<\/h5>/gi, '\n##### $1\n\n')
      .replace(/<h6[^>]*>(.*?)<\/h6>/gi, '\n###### $1\n\n');

    // Convert formatting
    markdown = markdown
      .replace(/<strong[^>]*>(.*?)<\/strong>/gi, '**$1**')
      .replace(/<b[^>]*>(.*?)<\/b>/gi, '**$1**')
      .replace(/<em[^>]*>(.*?)<\/em>/gi, '*$1*')
      .replace(/<i[^>]*>(.*?)<\/i>/gi, '*$1*')
      .replace(/<code[^>]*>(.*?)<\/code>/gi, '`$1`')
      .replace(
        /<pre[^>]*><code[^>]*>(.*?)<\/code><\/pre>/gi,
        '\n```\n$1\n```\n'
      );

    // Convert links
    markdown = markdown.replace(
      /<a[^>]*href="([^"]*)"[^>]*>(.*?)<\/a>/gi,
      '[$2]($1)'
    );

    // Convert blockquotes
    markdown = markdown.replace(
      /<blockquote[^>]*>(.*?)<\/blockquote>/gi,
      '\n> $1\n\n'
    );

    // Convert lists with improved handling
    markdown = convertListsToMarkdown(markdown);

    // Convert paragraphs and line breaks
    markdown = markdown
      .replace(/<p[^>]*>([\s\S]*?)<\/p>/gi, '\n$1\n\n')
      .replace(/<br[^>]*>/gi, '\n');

    // Remove any remaining HTML tags
    markdown = markdown.replace(/<[^>]*>/g, '');

    // Clean up excessive newlines and whitespace
    markdown = markdown
      .replace(/\n{3,}/g, '\n\n')
      .replace(/^\s+|\s+$/g, '')
      .trim();

    return markdown;
  } catch (error) {
    console.error('Error in fallback HTML to markdown conversion:', error);
    // Return cleaned HTML with tags removed as final fallback
    return html.replace(/<[^>]*>/g, '').trim();
  }
}

/**
 * Convert HTML lists to markdown with proper handling of nested structures
 */
function convertListsToMarkdown(html: string): string {
  // Handle unordered lists
  html = html.replace(/<ul[^>]*>([\s\S]*?)<\/ul>/gi, (match, listContent) => {
    const items = listContent.match(/<li[^>]*>([\s\S]*?)<\/li>/gi) || [];
    return (
      '\n' +
      items
        .map((item: string) => {
          const content = item
            .replace(/<li[^>]*>([\s\S]*?)<\/li>/gi, '$1')
            .trim();
          return content ? `- ${content}` : '';
        })
        .filter(Boolean)
        .join('\n') +
      '\n\n'
    );
  });

  // Handle ordered lists
  html = html.replace(/<ol[^>]*>([\s\S]*?)<\/ol>/gi, (match, listContent) => {
    const items = listContent.match(/<li[^>]*>([\s\S]*?)<\/li>/gi) || [];
    let index = 1;
    return (
      '\n' +
      items
        .map((item: string) => {
          const content = item
            .replace(/<li[^>]*>([\s\S]*?)<\/li>/gi, '$1')
            .trim();
          return content ? `${index++}. ${content}` : '';
        })
        .filter(Boolean)
        .join('\n') +
      '\n\n'
    );
  });

  return html;
}

/**
 * Enhanced markdown list parser with better structure handling
 */
export const parseMarkdownLists = (markdown: string): string => {
  try {
    if (!markdown) return '';

    // Process bold and italic formatting first
    let processedMarkdown = markdown
      .replace(/\*\*([^\*]+)\*\*/g, '<strong>$1</strong>')
      .replace(/\*([^\*]+)\*/g, '<em>$1</em>');

    const lines = processedMarkdown.split('\n');
    let html = '';
    let inList = false;
    let listStack: Array<{ type: string; indent: number }> = [];

    for (let i = 0; i < lines.length; i++) {
      const line = lines[i];
      const trimmedLine = line.trim();

      if (!trimmedLine) {
        // Empty line - close any open lists
        if (inList) {
          while (listStack.length > 0) {
            const list = listStack.pop();
            html += '</li>';
            html += list!.type === 'ol' ? '</ol>' : '</ul>';
          }
          inList = false;
        }
        continue;
      }

      // Check for list items
      const unorderedMatch = line.match(/^(\s*)-\s(.*)$/);
      const orderedMatch = line.match(/^(\s*)(\d+)\.\s(.*)$/);

      if (unorderedMatch || orderedMatch) {
        const match = unorderedMatch || orderedMatch;
        const indent = match![1].length;
        const content = (match![unorderedMatch ? 2 : 3] || '').trim();
        const isOrdered = !!orderedMatch;

        // Skip empty content to prevent extra bullets
        // Also skip content that only contains whitespace, zero-width spaces, or HTML entities
        if (
          !content ||
          content === '\u200B' ||
          content === '&nbsp;' ||
          /^\s*$/.test(content)
        ) {
          continue;
        }

        const type = isOrdered ? 'ol' : 'ul';

        if (!inList) {
          // Start new list
          html += `<${type}>`;
          inList = true;
          listStack.push({ type, indent });
          html += `<li>${content}</li>`;
        } else {
          // Handle nested lists
          const currentList = listStack[listStack.length - 1];

          if (indent > currentList.indent) {
            // Start nested list
            html = html.replace(/<\/li>$/, '');
            html += `<${type}><li>${content}</li></${type}></li>`;
            listStack.push({ type, indent });
          } else if (indent < currentList.indent) {
            // Close nested lists
            while (
              listStack.length > 0 &&
              listStack[listStack.length - 1].indent > indent
            ) {
              const list = listStack.pop();
              html += '</li>';
              html += list!.type === 'ol' ? '</ol>' : '</ul>';
            }
            html += `<li>${content}</li>`;
          } else {
            // Same level
            html += `<li>${content}</li>`;
          }
        }
      } else if (inList) {
        // Not a list item but we're in a list - append to current item
        html = html.replace(/<\/li>$/, ` ${trimmedLine}</li>`);
      } else {
        // Regular content
        html += `<p>${trimmedLine}</p>`;
      }
    }

    // Close any remaining open lists
    while (listStack.length > 0) {
      const list = listStack.pop();
      html += '</li>';
      html += list!.type === 'ol' ? '</ol>' : '</ul>';
    }

    // Final cleanup to remove any empty lists that might have been created
    html = html
      .replace(/<ul>\s*<\/ul>/g, '')
      .replace(/<ol>\s*<\/ol>/g, '')
      .replace(/<li>\s*<\/li>/g, '')
      .replace(/<li>&nbsp;<\/li>/g, '')
      .replace(/<li>\u200B<\/li>/g, '');

    return html;
  } catch (error) {
    console.error('Error parsing markdown lists:', error);
    return markdown;
  }
};

/**
 * Improved markdown to ContentState conversion
 */
export const markdownToContentState = (markdown: string): ContentState => {
  try {
    if (!markdown) return ContentState.createFromText('');

    // Preprocess markdown to handle empty list items
    let processedMarkdown = markdown
      .replace(/^(\s*)-\s*$/gm, '$1- \u200B')
      .replace(/^(\s*)\d+\.\s*$/gm, '$1$2. \u200B');

    return stateFromMarkdown(processedMarkdown);
  } catch (error) {
    console.error('Error converting markdown to ContentState:', error);
    return ContentState.createFromText(markdown);
  }
};

/**
 * Enhanced markdown to HTML conversion with better error handling
 */
export const markdownToHtml = (markdown: string): string => {
  try {
    if (!markdown) return '';

    let processedMarkdown = markdown.trim();

    // Check if markdown contains lists
    const hasLists = /^(\s*)-\s|\n(\s*)-\s|^(\s*)\d+\.\s|\n(\s*)\d+\.\s/.test(
      processedMarkdown
    );

    if (hasLists) {
      return processMarkdownWithLists(processedMarkdown);
    } else {
      return processMarkdownStandard(processedMarkdown);
    }
  } catch (error) {
    console.error('Error converting markdown to HTML:', error);
    return simpleMarkdownToHtml(markdown);
  }
};

/**
 * Process markdown that contains lists
 */
function processMarkdownWithLists(markdown: string): string {
  let processedHtml = '';
  let currentTextBlock = '';
  const lines = markdown.split('\n');

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];
    const isListItem = /^(\s*)-\s|^(\s*)\d+\.\s/.test(line);

    if (isListItem && currentTextBlock.trim()) {
      // Process accumulated text first
      try {
        const contentState = markdownToContentState(currentTextBlock);
        const rawContentState = convertToRaw(contentState);
        const htmlContent = draftToHtml(rawContentState);
        processedHtml += htmlContent;
      } catch (error) {
        console.warn(
          'Error processing text block, using simple conversion:',
          error
        );
        processedHtml += simpleMarkdownToHtml(currentTextBlock);
      }
      currentTextBlock = '';
    }

    if (isListItem) {
      // Collect list items
      let listBlock = line + '\n';
      let j = i + 1;

      while (j < lines.length && /^(\s*)-\s|^(\s*)\d+\.\s/.test(lines[j])) {
        listBlock += lines[j] + '\n';
        j++;
      }

      // Only process the list block if it contains actual content
      const processedListHtml = parseMarkdownLists(listBlock);
      if (processedListHtml.trim()) {
        processedHtml += processedListHtml;
      }
      i = j - 1;
    } else {
      currentTextBlock += line + '\n';
    }
  }

  // Process any remaining text
  if (currentTextBlock.trim()) {
    try {
      const contentState = markdownToContentState(currentTextBlock);
      const rawContentState = convertToRaw(contentState);
      const htmlContent = draftToHtml(rawContentState);
      processedHtml += htmlContent;
    } catch (error) {
      console.warn(
        'Error processing remaining text, using simple conversion:',
        error
      );
      processedHtml += simpleMarkdownToHtml(currentTextBlock);
    }
  }

  return processedHtml;
}

/**
 * Process standard markdown without lists
 */
function processMarkdownStandard(markdown: string): string {
  try {
    const contentState = markdownToContentState(markdown);
    const rawContentState = convertToRaw(contentState);
    return draftToHtml(rawContentState);
  } catch (error) {
    console.warn('Error with Draft.js conversion, using fallback:', error);
    return simpleMarkdownToHtml(markdown);
  }
}

/**
 * Simple markdown to HTML conversion as fallback
 */
function simpleMarkdownToHtml(markdown: string): string {
  if (!markdown) return '';

  let html = markdown
    // Headers
    .replace(/^# (.*$)/gm, '<h1>$1</h1>')
    .replace(/^## (.*$)/gm, '<h2>$1</h2>')
    .replace(/^### (.*$)/gm, '<h3>$1</h3>')
    .replace(/^#### (.*$)/gm, '<h4>$1</h4>')
    .replace(/^##### (.*$)/gm, '<h5>$1</h5>')
    .replace(/^###### (.*$)/gm, '<h6>$1</h6>')

    // Bold and italic
    .replace(/\*\*([^\*\n]+)\*\*/g, '<strong>$1</strong>')
    .replace(/\*([^\*\n]+)\*/g, '<em>$1</em>')
    .replace(/__([^_\n]+)__/g, '<strong>$1</strong>')
    .replace(/_([^_\n]+)_/g, '<em>$1</em>')

    // Links
    .replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<a href="$2">$1</a>')

    // Code
    .replace(/```([\s\S]*?)```/g, '<pre><code>$1</code></pre>')
    .replace(/`([^`]+)`/g, '<code>$1</code>')

    // Blockquotes
    .replace(/^> (.*$)/gm, '<blockquote>$1</blockquote>')

    // Line breaks and paragraphs
    .replace(/\n\n/g, '</p><p>')
    .replace(/\n/g, '<br>');

  // Wrap in paragraphs if needed
  if (
    html &&
    !html.startsWith('<h') &&
    !html.startsWith('<ul') &&
    !html.startsWith('<ol') &&
    !html.startsWith('<blockquote')
  ) {
    html = '<p>' + html + '</p>';
  }

  return html;
}

/**
 * Convert markdown to raw Draft.js content state
 */
export const markdownToRawDraftContent = (markdown: string): any => {
  try {
    if (!markdown) {
      return {
        blocks: [],
        entityMap: {},
      };
    }

    const contentState = markdownToContentState(markdown);
    return convertToRaw(contentState);
  } catch (error) {
    console.error('Error converting markdown to raw content:', error);
    return {
      blocks: [],
      entityMap: {},
    };
  }
};

/**
 * Prepare document content for AI with improved encoding handling
 */
export const prepareContentForAI = (documentContent: string): string => {
  console.log(`
===== PREPARING DOCUMENT CONTENT FOR AI =====
Document length: ${documentContent?.length || 0} characters
Document preview: ${documentContent ? documentContent.substring(0, 100) + '...' : 'No content'}
===========================================
  `);

  // Ensure proper decoding before conversion
  const decodedContent = HtmlProcessor.decodeHtmlEntities(documentContent);
  const markdown = htmlToMarkdown(decodedContent);

  if (markdown) {
    const headingPattern = /^(#{1,6})\s+(.+)$/gm;
    const headings: Array<RegExpExecArray> = [];
    let match: RegExpExecArray | null;

    while ((match = headingPattern.exec(markdown)) !== null) {
      headings.push(match);
    }

    if (headings.length > 0) {
      console.log(`
===== DOCUMENT STRUCTURE ANALYSIS =====
Found ${headings.length} headings:
${headings
  .slice(0, 5)
  .map((h) => `${h[1]} ${h[2]}`)
  .join('\n')}${headings.length > 5 ? '\n...(more headings)' : ''}
=====================================
      `);
    }
  }

  console.log(`
===== DOCUMENT CONVERTED TO MARKDOWN =====
Markdown length: ${markdown?.length || 0} characters
Markdown preview: ${markdown ? markdown.substring(0, 100) + '...' : 'No content'}
=========================================
  `);

  return markdown;
};

/**
 * Process AI response with better markdown extraction
 */
export const processAIResponse = (aiResponse: string): string => {
  const markdownPattern = /```(markdown|md)?\s*([\s\S]*?)```/i;
  const match = aiResponse.match(markdownPattern);

  let markdown = aiResponse;
  if (match && match[2]) {
    markdown = match[2].trim();
  }

  return markdownToHtml(markdown);
};

/**
 * Convert markdown TOC to HTML with improved formatting
 */
export const markdownTocToHtml = (markdown: string): string => {
  try {
    if (!markdown) return '';

    const lines = markdown.split('\n');
    let html = '';

    for (let i = 0; i < lines.length; i++) {
      const line = lines[i].trim();
      if (!line) continue;

      // Process different heading levels
      if (line.startsWith('# ')) {
        const headingText = line.substring(2).trim();
        html += `<h1 class="toc-main-heading">${headingText}</h1>`;
      } else if (line.startsWith('## ')) {
        const headingText = line.substring(3).trim();
        html += `<h2 class="toc-section-heading">${headingText}</h2>`;
      } else {
        // Process numbered sections and subsections
        const mainSectionMatch = line.match(/^(\d+)\.?\s+(.+)$/);
        const subSectionMatch = line.match(/^\s*-\s+(\d+)\.(\d+)\s+(.+)$/);
        const thirdLevelMatch = line.match(
          /^\s*-\s+(\d+)\.(\d+)\.(\d+)\s+(.+)$/
        );

        if (thirdLevelMatch) {
          const [, mainNum, subNum, thirdNum, content] = thirdLevelMatch;
          html += `<div class="toc-third-item">
            <span class="toc-number">${mainNum}.${subNum}.${thirdNum}</span> ${content}
          </div>`;
        } else if (subSectionMatch) {
          const [, mainNum, subNum, content] = subSectionMatch;
          html += `<div class="toc-sub-item">
            <span class="toc-number">${mainNum}.${subNum}</span> ${content}
          </div>`;
        } else if (mainSectionMatch && !line.startsWith('-')) {
          const [, number, content] = mainSectionMatch;
          html += `<div class="toc-main-item">
            <span class="toc-number">${number}.</span> ${content}
          </div>`;
        }
      }
    }

    // Add CSS styles
    const tocStyles = `
      <style>
        .toc-main-heading {
          font-size: 2rem;
          margin-top: 0.5rem;
          margin-bottom: 1.5rem;
          font-weight: 700;
          color: #222;
        }
        
        .toc-section-heading {
          font-size: 1.5rem;
          margin-top: 0.5rem;
          margin-bottom: 1.5rem;
          font-weight: 600;
          color: #222;
        }
        
        .toc-main-item {
          font-weight: 500;
          padding: 0.6rem 0 0.6rem 1rem;
          font-size: 1.05rem;
          color: #222;
        }
        
        .toc-sub-item {
          font-weight: normal;
          padding: 0.4rem 0 0.4rem 3rem;
          font-size: 0.95rem;
          color: #444;
        }
        
        .toc-third-item {
          font-weight: normal;
          padding: 0.3rem 0 0.3rem 5rem;
          font-size: 0.9rem;
          color: #555;
        }
        
        .toc-number {
          font-weight: 600;
          display: inline-block;
          min-width: 2rem;
          color: #333;
        }
      </style>
    `;

    return tocStyles + html;
  } catch (error) {
    console.error('Error converting TOC markdown to HTML:', error);
    return markdownToHtml(markdown);
  }
};
